import type { AdminOrderPaymentDto } from '@carcheck/shared';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { observer } from 'mobx-react-lite';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CaptureConfirmDialog, type CaptureDialogKind } from './CaptureConfirmDialog';
import { useHasPermission } from '../../../auth';
import { tokens } from '../../../design';
import { formatCurrency, formatRelative } from '../../../utils/format';
import {
  describeHoldWindow,
  isCapturable,
  isReconcilable,
  type HoldWindowPhase,
} from '../_lib/captureEligibility';
import { useCapturePayment } from '../_lib/useCapturePayment';
import { useReconcileCapture } from '../_lib/useReconcileCapture';

/**
 * AUT-223 (2S-17) — the operator's hand controls on the Платёж tab.
 *
 * Capture is the one step in the money flow with no user-facing retry: a
 * customer can pay again, a refund can be re-issued, a payout can be re-fired,
 * but an unsettled hold just expires at the bank while the order looks paid.
 * When the automatic path stalls, this is where somebody finishes the job.
 *
 * **One button at a time, by status.** `authorized` → capture; `capturing` →
 * reconcile. Never both, because they are never both applicable, and offering
 * "сверить" on a live hold invites an operator to go looking for a provider
 * record that was never created. This is UX ONLY — the server validates the
 * same transition and 409s a stale screen (`payment_not_capturable` /
 * `payment_not_reconcilable`).
 *
 * **Permission.** `process_payouts` — the same gate the endpoints carry
 * server-side and the same one `/admin/payouts`, `/admin/cards` and
 * `/admin/refunds` use. Not widened. No TOTP step-up: AUT-138 / AUT-139
 * removed that substrate from every admin action deliberately.
 *
 * **AUT-301 — an expired hold window WARNS, it does not disable.**
 * `holdExpiresAt` is `authorizedAt + escrow.holdDays`, OUR escrow policy, and
 * WebPay publishes no authorization expiry to compare it against; 7 days is
 * probably shorter than the bank's real lifetime. So the server no longer
 * refuses flat past the deadline — it ASKS the provider and settles if the hold
 * is alive. Greying the button out here would withhold an action that would
 * have worked, which is the more expensive mistake: the operator's fallback is
 * telling a customer to pay a second time for money that is still blocked. The
 * button stays live and carries the caveat instead — in the row AND in the
 * dialog, where the decision is actually made.
 *
 * On success the hooks invalidate `['admin','orders']`, so the tab refetches
 * and shows the new status instead of the one we opened with.
 */
const ActionButton = ({
  kind,
  busy,
  onClick,
}: {
  kind: CaptureDialogKind;
  busy: boolean;
  onClick: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Button
      variant="contained"
      color={kind === 'capture' ? 'primary' : 'warning'}
      disabled={busy}
      onClick={onClick}
      startIcon={kind === 'capture' ? <PaymentsIcon /> : <SyncProblemIcon />}
    >
      {t(`orders.capture.actions.${kind}`)}
    </Button>
  );
};

/**
 * The AUT-301 affordance: a caveat NEXT TO a button that stays pressable.
 * `closed` is red and `closing` amber, but neither disables anything — see the
 * note above for why withholding the action is the more expensive mistake.
 */
const HoldWindowCaveat = ({
  phase,
  text,
}: {
  phase: HoldWindowPhase;
  text: string;
}): ReactElement => (
  <Box
    data-testid="capture-hold-window-caveat"
    data-phase={phase}
    sx={{
      display: 'inline-flex',
      alignItems: 'flex-start',
      gap: 0.5,
      fontSize: 11,
      lineHeight: 1.4,
      color: phase === 'closed' ? tokens.state.danger : tokens.state.warning,
    }}
  >
    <WarningAmberIcon sx={{ fontSize: 13, mt: '1px' }} />
    {text}
  </Box>
);

export const CaptureActions = observer(
  ({
    payment,
    /** Injectable clock so the hold-window caveat is spec-testable. */
    now,
  }: {
    payment: AdminOrderPaymentDto;
    now?: number;
  }): ReactElement | null => {
    const { t } = useTranslation();
    const canProcess = useHasPermission('process_payouts');
    const [openKind, setOpenKind] = useState<CaptureDialogKind | null>(null);

    const capture = useCapturePayment();
    const reconcile = useReconcileCapture();

    const capturable = isCapturable(payment);
    const reconcilable = isReconcilable(payment);
    if (!canProcess || (!capturable && !reconcilable)) return null;

    const kind: CaptureDialogKind = capturable ? 'capture' : 'reconcile';
    const action = kind === 'capture' ? capture : reconcile;

    // Only the capture path cares about the clock: a `capturing` row is past
    // the hold question and reconcile moves no money either way.
    const { phase } = describeHoldWindow(payment.holdExpiresAt, now);
    const holdCaveat =
      capturable && phase !== 'open'
        ? t(`orders.capture.holdWindow.${phase}`, {
            when: formatRelative(payment.holdExpiresAt, now),
          })
        : null;

    const open = (): void => {
      action.reset();
      setOpenKind(kind);
    };

    const close = (): void => {
      action.reset();
      setOpenKind(null);
    };

    const onConfirm = (): void => {
      void action.run(payment.id).then((ok) => {
        // Only a success closes: a rejection leaves the dialog up with the
        // server's own explanation, which is the operator's next instruction.
        if (ok) setOpenKind(null);
      });
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <ActionButton kind={kind} busy={action.busy} onClick={open} />
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 11,
              color: tokens.text.tertiary,
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 12 }} />
            {t('orders.capture.auditNote')}
          </Box>
        </Box>

        {holdCaveat !== null ? <HoldWindowCaveat phase={phase} text={holdCaveat} /> : null}

        <CaptureConfirmDialog
          open={openKind !== null}
          kind={kind}
          amount={formatCurrency(Number(payment.amount), payment.currency)}
          warning={holdCaveat}
          loading={action.busy}
          error={action.error}
          onConfirm={onConfirm}
          onCancel={close}
        />
      </Box>
    );
  },
);
